import { useMemo } from "react";
import { SITE_URL, SITE_NAME, SITE_LOGO, SITE_LOGO_WIDTH, SITE_LOGO_HEIGHT } from "@/config/seo";

interface BreadcrumbItem {
  label: string;
  href?: string;
}

interface FAQItem {
  question: string;
  answer: string;
}

interface ArticleSchemaProps {
  title: string;
  description: string;
  image: string;
  dateModified?: string;
  datePublished?: string;
  url: string;
  author?: {
    name: string;
    slug: string;
    role?: string;
    sameAs?: string[];
  };
}

interface FAQSchemaProps {
  items: FAQItem[];
  pageUrl?: string;
  inLanguage?: string;
}

interface BreadcrumbSchemaProps {
  items: BreadcrumbItem[];
  baseUrl?: string;
}

interface WebSiteSchemaProps {
  name?: string;
  url?: string;
  searchUrl?: string;
}

interface OrganizationSchemaProps {
  name?: string;
  url?: string;
  logo?: string;
  description?: string;
  foundingDate?: string;
  sameAs?: string[];
}

// Article JSON-LD
export const ArticleJsonLd = ({ 
  title, 
  description, 
  image, 
  dateModified, 
  datePublished,
  url,
  author,
}: ArticleSchemaProps) => {
  const imageUrl = image.startsWith('http') ? image : `${SITE_URL}${image}`;
  
  const schema = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": title,
    "description": description,
    "image": {
      "@type": "ImageObject",
      "url": imageUrl,
      "width": 1200,
      "height": 630
    },
    "datePublished": datePublished || dateModified || new Date().toISOString().split('T')[0],
    "dateModified": dateModified || new Date().toISOString().split('T')[0],
    "url": url,
    "author": author
      ? {
          "@type": "Person",
          "name": author.name,
          "url": `${SITE_URL}/author/${author.slug}`,
          ...(author.role ? { "jobTitle": author.role } : {}),
          ...(author.sameAs && author.sameAs.length > 0 ? { "sameAs": author.sameAs } : {}),
        }
      : {
          "@type": "Organization",
          "name": SITE_NAME,
        },
    "publisher": {
      "@type": "Organization",
      "name": SITE_NAME,
      "logo": {
        "@type": "ImageObject",
        "url": SITE_LOGO,
        "width": SITE_LOGO_WIDTH,
        "height": SITE_LOGO_HEIGHT
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": url
    }
  }), [title, description, imageUrl, dateModified, datePublished, url, author]);

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

// Strip internal link syntax, markdown, and HTML to produce clean plain text
// Google's FAQ guidelines require answers as plain text (HTML allowed but no custom syntax)
const cleanFaqText = (raw: string): string => {
  if (!raw) return "";
  return raw
    // [[slug|Anchor text]] -> "Anchor text"
    .replace(/\[\[(?:term:|tool:)?[a-z0-9-]+\|([^\]]+)\]\]/gi, "$1")
    // [[slug]] -> "slug" turned into spaced words
    .replace(/\[\[(?:term:|tool:)?([a-z0-9-]+)\]\]/gi, (_, s) => s.replace(/-/g, " "))
    // Standard markdown link [text](url) -> text
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
    // Bold/italic/code markers
    .replace(/(\*\*|__)(.*?)\1/g, "$2")
    .replace(/(\*|_)(.*?)\1/g, "$2")
    .replace(/`([^`]+)`/g, "$1")
    // Heading hashes at line start
    .replace(/^#{1,6}\s+/gm, "")
    // Collapse whitespace
    .replace(/\s+/g, " ")
    .trim();
};

// FAQ JSON-LD — Google FAQPage rich result eligible
// Reference: https://developers.google.com/search/docs/appearance/structured-data/faqpage
export const FAQJsonLd = ({ items, pageUrl, inLanguage = "en" }: FAQSchemaProps) => {
  const schema = useMemo(() => {
    // Clean, dedupe by question, enforce min/max length per Google guidance
    const seen = new Set<string>();
    const cleaned = items
      .map(item => ({
        question: cleanFaqText(item.question),
        answer: cleanFaqText(item.answer),
      }))
      .filter(item => {
        if (!item.question || !item.answer) return false;
        if (item.answer.length < 10) return false;
        const key = item.question.toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });

    return {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      ...(pageUrl ? { "@id": `${pageUrl}#faq`, "mainEntityOfPage": pageUrl } : {}),
      "inLanguage": inLanguage,
      "mainEntity": cleaned.map((item, i) => ({
        "@type": "Question",
        ...(pageUrl ? { "@id": `${pageUrl}#faq-${i + 1}` } : {}),
        "name": item.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": item.answer,
          "inLanguage": inLanguage,
        },
      })),
    };
  }, [items, pageUrl, inLanguage]);

  if (!schema.mainEntity || schema.mainEntity.length === 0) return null;

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

// Breadcrumb JSON-LD
export const BreadcrumbJsonLd = ({ items, baseUrl = SITE_URL }: BreadcrumbSchemaProps) => {
  const schema = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": baseUrl
      },
      ...items.map((item, index) => ({
        "@type": "ListItem",
        "position": index + 2,
        "name": item.label,
        ...(item.href ? { "item": `${baseUrl}${item.href}` } : {})
      }))
    ]
  }), [items, baseUrl]);

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

// WebSite JSON-LD (for homepage)
export const WebSiteJsonLd = ({ 
  name = SITE_NAME, 
  url = SITE_URL, 
  searchUrl = `${SITE_URL}/search` 
}: WebSiteSchemaProps) => {
  const schema = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": name,
    "url": url,
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": `${searchUrl}?q={search_term_string}`
      },
      "query-input": "required name=search_term_string"
    }
  }), [name, url, searchUrl]);

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

// Organization JSON-LD (for homepage)
export const OrganizationJsonLd = ({ 
  name = SITE_NAME, 
  url = SITE_URL, 
  logo = SITE_LOGO,
  description = "Clear, practical guidance for adults learning everyday life skills. No judgment, no fluff—just the information you need.",
  foundingDate = "2024",
  sameAs = []
}: OrganizationSchemaProps) => {
  const schema = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": name,
    "url": url,
    "logo": {
      "@type": "ImageObject",
      "url": logo,
      "width": SITE_LOGO_WIDTH,
      "height": SITE_LOGO_HEIGHT
    },
    "description": description,
    "foundingDate": foundingDate,
    ...(sameAs.length > 0 && { "sameAs": sameAs })
  }), [name, url, logo, description, foundingDate, sameAs]);

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

// HowTo JSON-LD (for guide/how-to articles)
interface HowToSchemaProps {
  title: string;
  description: string;
  image: string;
  sections: Array<{ type: string; title?: string; content?: string | object }>;
}

export const HowToJsonLd = ({ title, description, image, sections }: HowToSchemaProps) => {
  const steps = sections
    .filter(s => s.type === 'heading' && s.title)
    .map((s, i) => ({
      "@type": "HowToStep",
      "position": i + 1,
      "name": s.title,
      ...(typeof s.content === 'string' ? { "text": s.content.substring(0, 300) } : {})
    }));

  const schema = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": title,
    "description": description,
    "image": image.startsWith('http') ? image : `${SITE_URL}${image}`,
    "step": steps
  }), [title, description, image, steps]);

  if (steps.length < 3) return null;

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

// Speakable JSON-LD — used by Google Assistant + AI search to read short answer blocks aloud.
// Pass CSS selectors that point to the most citable, self-contained answer chunks
// (typically the TL;DR block and the FAQ answers).
interface SpeakableSchemaProps {
  url: string;
  cssSelectors?: string[];
  xpaths?: string[];
}

export const SpeakableJsonLd = ({
  url,
  cssSelectors = [".tldr-answer", ".faq-answer"],
  xpaths,
}: SpeakableSchemaProps) => {
  const schema = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": url,
    "url": url,
    "speakable": {
      "@type": "SpeakableSpecification",
      ...(cssSelectors.length > 0 ? { "cssSelector": cssSelectors } : {}),
      ...(xpaths && xpaths.length > 0 ? { "xpath": xpaths } : {}),
    },
  }), [url, cssSelectors, xpaths]);

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

// CollectionPage JSON-LD — used on hub pages to signal that the page curates a
// list of related Article items. Improves how AI assistants understand hubs as
// topical authorities rather than thin link pages.
interface CollectionPageItem {
  url: string;
  name: string;
  description?: string;
}

interface CollectionPageSchemaProps {
  url: string;
  name: string;
  description: string;
  items: CollectionPageItem[];
  inLanguage?: string;
}

export const CollectionPageJsonLd = ({
  url,
  name,
  description,
  items,
  inLanguage = "en",
}: CollectionPageSchemaProps) => {
  const schema = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "@id": url,
    "url": url,
    "name": name,
    "description": description,
    "inLanguage": inLanguage,
    "isPartOf": {
      "@type": "WebSite",
      "name": SITE_NAME,
      "url": SITE_URL,
    },
    "mainEntity": {
      "@type": "ItemList",
      "numberOfItems": items.length,
      "itemListElement": items.map((it, i) => ({
        "@type": "ListItem",
        "position": i + 1,
        "url": it.url,
        "name": it.name,
        ...(it.description ? { "description": it.description } : {}),
      })),
    },
  }), [url, name, description, items, inLanguage]);

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

// Helper to extract FAQ items from article sections
export const extractFAQFromSections = (sections: Array<{ type: string; questions?: Array<{ question: string; answer: string }> }>): FAQItem[] => {
  const faqItems: FAQItem[] = [];
  
  sections.forEach(section => {
    if (section.type === 'faq' && section.questions) {
      section.questions.forEach(q => {
        faqItems.push({
          question: q.question,
          answer: q.answer
        });
      });
    }
  });
  
  return faqItems;
};
